import { keepPreviousData, useQuery } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'
import type {
  BroadcastAudience,
  BroadcastListItem,
  BroadcastListResult,
  BroadcastPreviewResult,
} from '@ayub/shared'
import {
  BROADCASTS_PAGE_SIZE,
  fetchBroadcast,
  fetchBroadcasts,
  previewBroadcastAudience,
} from './api'

/**
 * `manual` auditoriya bo'sh ro'yxat bilan serverga yuborilsa 400 qaytaradi
 * (`services/broadcast.ts`: `validateBroadcastAudience()`). Bu yerda OLDINDAN tekshiriladi —
 * admin hali hech kimni tanlamagan paytda har harfda (yo'q, har checkbox bosishda) 400
 * xatosi bilan so'rov otilib turmasin, buning o'rniga oddiygina "hech kim tanlanmagan"
 * holati ko'rsatiladi (`AudienceSection.tsx`).
 */
export function isAudienceReady(audience: BroadcastAudience): boolean {
  return audience.kind !== 'manual' || audience.bloggerIds.length > 0
}

/**
 * "Kimga" bo'limidagi jonli sonlar (`docs/03-app-flow.md`: "Barcha faol blogerlar (442)").
 * `queryKey`ga butun `audience` obyekti kiritilgan — TanStack Query kalitni CHUQUR
 * (JSON) taqqoslaydi, shuning uchun `stage` yoki `bloggerIds` o'zgarganda ham (massiv
 * REFERENSI har safar yangi bo'lsa ham, MAZMUNI bir xil bo'lsa) keshdan qaytadi, mazmun
 * o'zgarsa esa avtomatik qayta so'raladi — vazifa talabi "audience o'zgarganda qayta
 * so'ralsin" aynan shu orqali bajariladi, qo'lda `useEffect`/`refetch` chaqirmasdan.
 */
export function useBroadcastPreview(audience: BroadcastAudience) {
  const ready = isAudienceReady(audience)

  const query = useQuery<BroadcastPreviewResult, Error>({
    queryKey: ['broadcasts', 'preview', audience],
    queryFn: () => previewBroadcastAudience(audience),
    enabled: ready,
    placeholderData: keepPreviousData,
  })

  return {
    // `ready=false` bo'lganda `query.data` eski (boshqa auditoriyadan qolgan) qiymatni
    // ko'rsatishi mumkin — shu holatda ataylab `undefined` qaytariladi, "0 ta odam" deb
    // yolg'on ko'rsatmaslik uchun (`AudienceSection.tsx` buni alohida matn bilan izohlaydi).
    preview: ready ? query.data : undefined,
    isLoading: ready && query.isLoading,
    isFetching: ready && query.isFetching,
    error: ready && query.isError ? query.error : null,
    // Xato bo'lganda "Qayta urinish" tugmasi uchun (`AudienceSection.tsx`) — boshqa
    // barcha modullardagi izchil naqsh: xato holatida ham qayta urinish imkoniyati bo'lsin.
    refetch: query.refetch,
  }
}

/** "Tarix" bo'limi — `GET /api/broadcasts`, sahifalangan, yangisi birinchi (server tartiblaydi). */
export function useBroadcastList(page: number) {
  const query = useQuery<BroadcastListResult, Error>({
    queryKey: ['broadcasts', 'list', page],
    queryFn: () => fetchBroadcasts({ page, pageSize: BROADCASTS_PAGE_SIZE }),
    placeholderData: keepPreviousData,
  })

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.isError ? query.error : null,
    refetch: query.refetch,
  }
}

/**
 * Yuborish progressi — `GET /api/broadcasts/:id`ni davriy so'raydi. Bu loyihada ILK
 * `refetchInterval` FUNKSIYA shaklida ishlatilishi: broadcast holati `'pending'` yoki
 * `'sending'` bo'lganda 1.5 soniyada bir yangilanadi, `'done'`/`'failed'`ga yetgach `false`
 * qaytarib avtomatik TO'XTAYDI. Doimiy son (masalan `refetchInterval: 1500`) bo'lganda
 * so'rov abadiy davom etib, tugagan broadcast uchun ham foydasiz pollingni davom
 * ettirardi — funksiya shakli oxirgi ma'lum holatga (`query.state.data`) qarab qaror
 * qiladi (`@tanstack/query-core` tipida: `(query: Query<...>) => number | false`).
 *
 * ⚠️ MUHIM TUZATISH: avval bu yerda faqat `q.state.data?.status` tekshirilardi — agar
 * so'rovning O'ZI xato bilan tugasa (masalan brauzerda sessiya tugab 401 kelsa), `data`
 * OLDINGI muvaffaqiyatli javobda (hali `'sending'`) qolganidan foydalanib, funksiya baribir
 * `1500`ni qaytarardi — ya'ni polling XATO bo'lgan so'rovni har 1,5 soniyada QAYTA-QAYTA
 * yuborardi (masalan 401 spam). Endi `q.state.error`ga qaraladi: joriy urinish xato bilan
 * tugagan bo'lsa, holatidan qat'i nazar `false` qaytariladi — polling to'xtaydi, xato
 * `error` orqali chaqiruvchiga (`ProgressDialog`) yuboriladi.
 *
 * `id === null` bo'lganda so'rov umuman yuborilmaydi (`enabled: false`) — sahifa hali
 * hech qanday broadcast yaratmagan yoki progress oynasi yopilgan holatga mos.
 */
export function useBroadcastProgress(id: number | null) {
  const query = useQuery<BroadcastListItem, Error>({
    queryKey: ['broadcasts', 'detail', id],
    queryFn: () => fetchBroadcast(id as number),
    enabled: id !== null,
    refetchInterval: (q) => {
      if (q.state.error) return false
      const status = q.state.data?.status
      return status === 'pending' || status === 'sending' ? 1500 : false
    },
  })

  return {
    broadcast: query.data ?? null,
    isLoading: query.isLoading,
    error: query.isError ? query.error : null,
  }
}

/**
 * Yangi broadcast yaratilgandan keyin "Tarix" ro'yxati eskiradi — `payouts/hooks.ts`dagi
 * `invalidatePayoutRelatedCaches()` bilan bir xil naqsh (predicate orqali shu bo'limning
 * BARCHA keshlarini, sahifa raqamidan qat'i nazar, eskirtiradi).
 */
export function invalidateBroadcastCaches(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({
    predicate: (query) => query.queryKey[0] === 'broadcasts',
  })
}
